// @section 1 TrendChart — hand-rolled SVG line chart (ported from OptiqOS
// agency-report/shared/TrendChart, re-themed to V14 tokens + made generic).
// Why SVG not Recharts: total pixel control = the clean look. One metric shown
// at a time with a toggle; area gradient; adaptive dot density; horizontal-only
// grid; capped x-labels; cursor tooltip with a dashed vertical guide.
//
// Generic over two metric kinds:
//   count → sums an additive base field per bucket
//   rate  → sums numerator + denominator per bucket, divides (correct rate
//           aggregation — never averages rates)

import { useState, useMemo, useRef } from 'react';

// @section 2 Types
export type TrendMetric =
  | { key: string; label: string; kind: 'count'; field: string; color: string }
  | { key: string; label: string; kind: 'rate'; num: string; den: string; color: string };

// date is a string; all other fields are additive numbers. The index signature
// must admit string|number so `date` is compatible — numeric reads cast to number.
export type TrendRow = { date: string; [field: string]: number | string };
type Granularity = 'daily' | 'weekly' | 'monthly';

const num = (v: number | string | undefined): number => (typeof v === 'number' ? v : 0);

interface Props {
  data: TrendRow[];          // daily rows with the additive base fields
  metrics: TrendMetric[];    // toggleable; first is the default
  baseFields: string[];      // additive fields summed on aggregation
  title?: string;
  enableGranularity?: boolean;
}

// @section 3 Aggregation helpers (ISO week / month bucketing)
function isoWeekKey(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dow);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}
function monthKey(dateStr: string): string { return dateStr.slice(0, 7); }

function aggregate(data: TrendRow[], baseFields: string[], g: Granularity): TrendRow[] {
  if (g === 'daily') return data;
  const keyFn = g === 'weekly' ? isoWeekKey : monthKey;
  const buckets = new Map<string, TrendRow>();
  for (const row of data) {
    const key = keyFn(row.date);
    const existing = buckets.get(key);
    if (existing) {
      for (const f of baseFields) existing[f] = num(existing[f]) + num(row[f]);
    } else {
      const seed: TrendRow = { date: key };
      for (const f of baseFields) seed[f] = num(row[f]);
      buckets.set(key, seed);
    }
  }
  return [...buckets.values()];
}

// @section 4 Formatters
function formatLabel(s: string, g: Granularity): string {
  if (g === 'weekly') return s.split('-')[1] ?? s;
  if (g === 'monthly') {
    const [y, m] = s.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m!, 10) - 1]} '${y!.slice(2)}`;
  }
  return s.slice(5); // MM-DD
}
function fmtCount(v: number): string {
  return v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K` : `${Math.round(v)}`;
}
function metricValue(bucket: TrendRow, m: TrendMetric): number {
  if (m.kind === 'count') return num(bucket[m.field]);
  const den = num(bucket[m.den]);
  return den > 0 ? +(100 * num(bucket[m.num]) / den).toFixed(1) : 0;
}
function fmtMetric(v: number, m: TrendMetric): string {
  return m.kind === 'rate' ? `${v}%` : fmtCount(v);
}

// @section 5 Component
export default function TrendChart({ data, metrics, baseFields, title = 'Trend', enableGranularity = true }: Props) {
  const [metricKey, setMetricKey] = useState(metrics[0]?.key ?? '');
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const metric = metrics.find((m) => m.key === metricKey) ?? metrics[0];
  const agg = useMemo(() => aggregate(data, baseFields, granularity), [data, baseFields, granularity]);

  // @block 5.1 Empty state
  if (!metric || agg.length < 2) {
    return (
      <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-paper-100/80">{title}</h3>
        </div>
        <p className="py-8 text-center text-sm text-muted">Not enough data for a trend.</p>
      </div>
    );
  }

  // @block 5.2 Geometry
  const W = 820, H = 220;
  const PAD = { top: 18, right: 16, bottom: 30, left: 52 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const yTicks = 4;

  const values = agg.map((d) => metricValue(d, metric));
  const maxVal = Math.max(...values, metric.kind === 'rate' ? 5 : 1);

  const points = agg.map((d, i) => ({
    x: PAD.left + (i / (agg.length - 1)) * chartW,
    y: PAD.top + chartH - (metricValue(d, metric) / maxVal) * chartH,
    row: d,
  }));
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x},${PAD.top + chartH} L${points[0].x},${PAD.top + chartH} Z`;

  const labelCount = Math.min(8, agg.length);
  const labelIdx = Array.from({ length: labelCount }, (_, i) =>
    Math.round((i / (labelCount - 1)) * (agg.length - 1)));

  const gradId = `tc-${title.replace(/\W/g, '')}-${metric.key}`;
  const color = metric.color;

  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 relative">
      {/* @block 5.3 Header — title + granularity + metric toggle */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-paper-100/80">{title}</h3>
        <div className="flex items-center gap-3">
          {enableGranularity && (
            <div className="flex gap-0.5 rounded-md bg-ink-800/60 p-0.5">
              {(['daily', 'weekly', 'monthly'] as const).map((g) => (
                <button key={g} onClick={() => setGranularity(g)}
                  className={`rounded px-2 py-0.5 text-[10px] font-medium transition ${
                    granularity === g ? 'bg-ink-700 text-paper-100/90' : 'text-muted hover:text-paper-100/70'
                  }`}>
                  {g[0].toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-1 flex-wrap">
            {metrics.map((m) => (
              <button key={m.key} onClick={() => setMetricKey(m.key)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                  m.key === metric.key ? 'text-ink-950' : 'text-muted hover:text-paper-100/70'
                }`}
                style={m.key === metric.key ? { backgroundColor: m.color } : undefined}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* @block 5.4 SVG */}
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto"
           preserveAspectRatio="xMidYMid meet"
           onMouseLeave={() => { setHoveredIdx(null); setTip(null); }}>
        {/* Horizontal grid + Y labels */}
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const val = (maxVal / yTicks) * i;
          const y = PAD.top + chartH - (val / maxVal) * chartH;
          return (
            <g key={`y-${i}`}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />
              <text x={PAD.left - 6} y={y + 3} textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize={10}>
                {fmtMetric(val, metric)}
              </text>
            </g>
          );
        })}

        {/* Area + line */}
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} />

        {/* Hover zones + dots */}
        {points.map((p, idx) => {
          const colW = chartW / (agg.length - 1 || 1);
          return (
            <g key={`pt-${idx}`}>
              <rect x={p.x - colW / 2} y={PAD.top} width={colW} height={chartH} fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  setHoveredIdx(idx);
                  if (svgRef.current) { const r = svgRef.current.getBoundingClientRect(); setTip({ x: e.clientX - r.left, y: e.clientY - r.top }); }
                }}
                onMouseMove={(e) => {
                  if (svgRef.current) { const r = svgRef.current.getBoundingClientRect(); setTip({ x: e.clientX - r.left, y: e.clientY - r.top }); }
                }} />
              {hoveredIdx === idx && (
                <line x1={p.x} x2={p.x} y1={PAD.top} y2={PAD.top + chartH} stroke="rgba(255,255,255,0.15)" strokeDasharray="3,3" />
              )}
              <circle cx={p.x} cy={p.y} r={hoveredIdx === idx ? 4 : agg.length > 60 ? 1 : 2.5} fill={color} />
            </g>
          );
        })}

        {/* X labels (capped at 8) */}
        {labelIdx.map((idx) => {
          const p = points[idx]!;
          return (
            <text key={`xl-${idx}`} x={p.x} y={H - 6} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize={10}>
              {formatLabel(p.row.date, granularity)}
            </text>
          );
        })}
      </svg>

      {/* @block 5.5 Tooltip — selected metric hero + base-field context */}
      {hoveredIdx !== null && tip && (() => {
        const d = agg[hoveredIdx]!;
        const dateLabel = granularity === 'daily'
          ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : granularity === 'weekly' ? `Week ${d.date.split('-')[1]}` : formatLabel(d.date, 'monthly');
        return (
          <div className="pointer-events-none absolute z-10 rounded-lg border border-ink-700 bg-ink-950/95 px-3 py-2.5 shadow-xl backdrop-blur-sm"
            style={{ left: Math.min(tip.x + 12, (svgRef.current?.clientWidth ?? 800) - 170), top: tip.y - 10, minWidth: 150 }}>
            <p className="text-[10px] font-medium text-muted mb-1.5">{dateLabel}</p>
            <div className="space-y-0.5 text-[11px]">
              <div className="flex justify-between gap-4">
                <span style={{ color }}>{metric.label}</span>
                <span className="tabular-nums font-semibold" style={{ color }}>{fmtMetric(metricValue(d, metric), metric)}</span>
              </div>
              {metrics.filter((m) => m.key !== metric.key).slice(0, 5).map((m) => (
                <div key={m.key} className="flex justify-between gap-4">
                  <span className="text-muted">{m.label}</span>
                  <span className="tabular-nums text-paper-100/80">{fmtMetric(metricValue(d, m), m)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
